import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, EventCategoryRow, EventDetail } from '../../api';
import { eventEndpoints } from '../../api';
import { usePermissions } from '../../session';
import { Badge, Button, ErrorNote, Panel } from '../../ui';
import { INPUT } from './shared';

/**
 * Which categories an event is in (D-155).
 *
 * An event used to hold one `category_id`, so a comedy night that is also a
 * festival date had to pick one and went missing from the other filter. It is a
 * set now, and the **primary** — the one shown where there is room for a single
 * label — is the lowest-ordered of them rather than a separate choice somebody
 * has to keep in step.
 *
 * A new category can be made from here, because the alternative was a migration
 * every time the venue booked a kind of show it had not booked before.
 */
export function CategoriesPanel({ detail, onError }: { detail: EventDetail; onError: (error: ApiError) => void }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const editable = can('events.manage');
  const catalog = useQuery({ queryKey: ['event-categories'], queryFn: () => eventEndpoints.categories() });
  const [selected, setSelected] = useState<string[]>(() => detail.categories?.map((entry) => entry.id) ?? []);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelected(detail.categories?.map((entry) => entry.id) ?? []);
  }, [detail.categories]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['event', detail.event.id] });
    void queryClient.invalidateQueries({ queryKey: ['event-categories'] });
  };

  const save = useMutation({
    mutationFn: (ids: string[]) => eventEndpoints.update(detail.event.id, { category_ids: ids }),
    onSuccess: refresh,
    onError,
  });

  const create = useMutation({
    mutationFn: () => eventEndpoints.createCategory({ name: newName.trim() }),
    onSuccess: (result) => {
      setNewName('');
      setAdding(false);
      setError(null);
      // A category made here is one somebody wants on this event.
      const next = [...selected, result.category.id];
      setSelected(next);
      save.mutate(next);
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const options = catalog.data?.categories ?? [];
  const toggle = (category: EventCategoryRow) => {
    const next = selected.includes(category.id) ? selected.filter((id) => id !== category.id) : [...selected, category.id];
    setSelected(next);
    save.mutate(next);
  };
  // The primary is the lowest-ordered one selected, which is exactly how the
  // API derives it — shown so nobody wonders which label the storefront picks.
  const primary = options.filter((entry) => selected.includes(entry.id))[0] ?? null;

  return (
    <Panel
      title="Categories"
      description="Every category this event belongs to. The first one, in catalog order, is the primary — the single label a listing row shows."
      actions={editable && !adding ? <Button onClick={() => setAdding(true)}>New category</Button> : null}
    >
      <div className="space-y-4 px-5 py-4">
        {error ? <ErrorNote>{error}</ErrorNote> : null}

        {adding ? (
          <form
            className="flex flex-wrap items-end gap-3 border border-line bg-page p-3"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <label className="text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Name</span>
              <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Tribute band" className={`mt-1 w-56 ${INPUT}`} autoFocus />
            </label>
            <Button type="submit" variant="primary" disabled={newName.trim().length === 0 || create.isPending}>
              {create.isPending ? 'Adding…' : 'Add and apply'}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setAdding(false);
                setNewName('');
                setError(null);
              }}
            >
              Cancel
            </Button>
            <p className="text-xs text-ink-400">The web address is made from the name: customers filter at /events?category=…</p>
          </form>
        ) : null}

        {catalog.isPending ? (
          <p className="text-sm text-ink-400">Loading…</p>
        ) : options.length === 0 ? (
          <p className="text-sm text-ink-400">No categories yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {options.map((category) => {
              const on = selected.includes(category.id);
              return (
                <button
                  key={category.id}
                  type="button"
                  disabled={!editable || save.isPending}
                  onClick={() => toggle(category)}
                  aria-pressed={on}
                  className={`rounded-full px-3 py-1.5 text-sm ${on ? 'bg-accent text-surface' : 'border border-line text-ink-600 hover:border-accent hover:text-accent'} disabled:opacity-50`}
                >
                  {category.name}
                </button>
              );
            })}
          </div>
        )}

        <p className="text-xs text-ink-400">
          {selected.length === 0 ? (
            'In no category: it will not appear under any filter on the calendar.'
          ) : (
            <>
              Primary: <Badge tone="accent">{primary?.name ?? '—'}</Badge> {selected.length > 1 ? `· and ${selected.length - 1} more` : null}
            </>
          )}
        </p>
      </div>
    </Panel>
  );
}
